import React, {ReactNode, useState, useEffect} from "react";
import classNames from "classnames";
import {Box, Chip} from "@mui/material";
import {IconProduct} from "../utils/icons";
import {PILL_SX} from "../pages/videoProduction/helper";
import {toast} from "react-hot-toast";
import BrandInlineList from "./brand/BrandInlineList";

interface ChipItem {
    label: ReactNode;
    variant?: "default" | "secondary";
    chipSx?: Record<string, any>;
    // Chip-level action (e.g. open a signed document). Stops propagation so
    // the card's own onClick doesn't also fire.
    onClick?: () => void;
}

export interface ListItemCardProps {
    image?: string | null;
    imageCount?: number;
    title: string;
    subtitle?: string | null;
    associationIds?: string;
    brands?: Array<{
        id?: number;
        name?: string;
        logo?: string;
        rmn?: boolean;
        [key: string]: any;
    }>;
    brandsNavigable?: boolean;
    chips?: ChipItem[];
    rightContent?: ReactNode;
    onClick?: () => void;
}

const ListItemCard = ({
    image,
    imageCount,
    title,
    subtitle,
    associationIds,
    brands,
    brandsNavigable,
    chips,
    rightContent,
    onClick,
}: ListItemCardProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        setImageFailed(false);
    }, [image]);

    const handleIdClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (associationIds) {
            navigator.clipboard.writeText(associationIds);
            toast.success("ID copied to clipboard");
        }
    };

    return (
        <div
            className={classNames(
                "w-full max-w-full overflow-hidden rounded-[16px] border border-border-token bg-surface p-3 sm:p-4 cursor-pointer",
                {"border-accent": isHovered},
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onClick}
        >
            <div className="flex items-start justify-between gap-3 sm:gap-4 w-full min-w-0 flex-col lg:flex-row lg:items-center">
                <div className="flex gap-3 sm:gap-4 items-start min-w-0 w-full flex-1">
                    <Box
                        className={classNames(
                            "flex w-[72px] h-[72px] sm:w-[112px] sm:h-[112px] items-center justify-center object-cover rounded-2xl overflow-hidden relative shrink-0",
                            // Uploaded artwork usually has white baked in, so give it a
                            // deliberate white plate instead of letting it read as a
                            // ragged hole in the dark card. Empty state keeps the tint.
                            image && !imageFailed ? "bg-white" : "bg-tint",
                        )}
                    >
                        {image && !imageFailed ? (
                            <img
                                src={image}
                                alt="product icon"
                                className="h-full w-full object-cover rounded-2xl"
                                onError={() => setImageFailed(true)}
                            />
                        ) : (
                            <IconProduct />
                        )}
                        {imageCount != null && imageCount > 1 && (
                            <div className="absolute bottom-1 right-1 bg-accent text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                {imageCount}
                            </div>
                        )}
                    </Box>
                    <div className="flex flex-col items-start gap-1 min-w-0 flex-1 w-full">
                        <div className="flex items-center gap-2 overflow-hidden flex-wrap w-full min-w-0">
                            <div className="min-w-0 max-lg:flex-1 max-lg:min-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap text-text-1 font-bold text-sm sm:text-base leading-[132%]">
                                {title}
                            </div>
                            {associationIds && (
                                <div
                                    className="bg-accent text-white font-bold text-xs rounded-md hover:bg-[#735C38] cursor-pointer p-1 px-2 shrink-0 whitespace-nowrap"
                                    title="Copy ID"
                                    onClick={handleIdClick}
                                >
                                    {associationIds}
                                </div>
                            )}
                            {brands && (
                                <BrandInlineList
                                    brands={brands}
                                    navigable={brandsNavigable}
                                />
                            )}
                        </div>
                        {subtitle && (
                            <div
                                className="text-text-2 font-circular text-sm leading-[132%]"
                                style={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    wordBreak: "break-word",
                                }}
                            >
                                {subtitle}
                            </div>
                        )}
                        {chips && chips.length > 0 && (
                            <div className="flex gap-2 items-center flex-wrap mt-1 min-w-0 max-w-full">
                                {chips.map((chip, i) => (
                                    <Chip
                                        key={i}
                                        label={chip.label}
                                        onClick={
                                            chip.onClick
                                                ? (e) => {
                                                      e.stopPropagation();
                                                      chip.onClick!();
                                                  }
                                                : undefined
                                        }
                                        sx={{
                                            ...PILL_SX,
                                            maxWidth: "100%",
                                            ...(chip.variant ===
                                                "secondary" && {
                                                // --track is a step darker than PILL_SX's
                                                // --surface-2 in both themes, preserving the
                                                // distinction #E8E8E8 gave against #F4F4F4.
                                                backgroundColor: "var(--track)",
                                            }),
                                            ...chip.chipSx,
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {rightContent && (
                    <div
                        className="flex items-center gap-2 sm:gap-4 justify-end flex-wrap w-full min-w-0 lg:w-auto lg:flex-nowrap lg:justify-center lg:flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {rightContent}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ListItemCard;
